import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import {
  type FindOrderSummaryResponse,
  type OrderMode,
  PaymentChannel,
} from '../../../../../resources/@types/courseSchema';

import { decodeHTMLEntities } from '../../../../lib/misc';
import { OrderStatus } from './OrderStatus';

type OrderSummaryItem = Required<FindOrderSummaryResponse>['items'][0];

/**
 * snake case를 camel case로 변경하고
 * _ 객체를 제거합니다.
 */
export default class OrderDTO {
  readonly id: number;
  readonly createdAt: string;
  readonly status: keyof typeof OrderStatus;

  readonly title: string;
  readonly payPrice: number;
  readonly regPrice: number;
  readonly receiptUrl: string | null;
  readonly currency: string;
  readonly createdAtUTC: string;
  readonly timezone: string;
  readonly isReceiptAvailable: boolean;
  readonly canGetReceipt: boolean;
  readonly canGetInvoice: boolean;
  readonly paymentChannel: string;
  readonly paymentMethod: NonNullable<OrderSummaryItem['paymentMethodType']>;
  readonly orderMode: keyof typeof OrderMode;
  /**
   * 주문한 강의 수
   */
  readonly itemsCount: number;
  /**
   * 패키지 주문의 경우 백엔드가 내려주는 패키지 내 강의 총 정가 합계.
   */
  readonly totalCourseAmount: number | null;

  private _response: OrderSummaryItem;

  constructor(response: OrderSummaryItem) {
    this._response = response;
    this.id = response.id;
    this.createdAt = dayjs(response.createdAt).format('YYYY.MM.DD');
    this.status = response.status as OrderDTO['status'];
    this.title = decodeHTMLEntities(response.title ?? '');

    this.payPrice = response.paymentPrice ?? 0;
    this.regPrice = response.regularPrice ?? 0;
    this.totalCourseAmount = response.totalCourseAmount ?? null;
    this.receiptUrl = response.receiptUrl;
    this.itemsCount = response?.itemsCount ?? 0;
    this.currency = response.currency;
    this.createdAtUTC = response.createdAtUTC;
    this.timezone = response.timezone;
    this.isReceiptAvailable = response.isReceiptAvailable;
    this.canGetReceipt = response.canGetReceipt;
    this.canGetInvoice = response.canGetInvoice;
    this.paymentChannel = response.paymentChannel;
    this.orderMode = response.orderMode;
    this.paymentMethod = response.paymentMethodType || 'NONE';
  }

  get statusLabel() {
    return this.status === 'WC_COMPLETED' ? OrderStatus.PAYMENT_COMPLETE : OrderStatus[this.status];
  }

  get displayRegularPrice() {
    if (this.orderMode === 'COURSE_PACKAGE' && !isNil(this.totalCourseAmount)) {
      return this.totalCourseAmount;
    }
    return this.regPrice;
  }

  get discountedPrice() {
    if (isNil(this.displayRegularPrice) || isNil(this.payPrice)) {
      return null;
    }

    return this.displayRegularPrice - this.payPrice;
  }

  /**
   * 정상 토스 페이팔 결제는 영수증이 항상 존재한다. 영수증이 없는 페이팔(PLATFORM) 건은
   * 구매내역 상세를 조회할 수 없는 해외 특이케이스이므로 상세 진입을 막는 데 사용한다.
   */
  get isPaypalOverseasWithoutReceipt() {
    return (
      this.paymentChannel === PaymentChannel.PAYPAL &&
      this.paymentMethod === 'PLATFORM' &&
      !this.canGetReceipt &&
      !this.isReceiptAvailable
    );
  }
}
